import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import DashboardTab    from "./budget/DashboardTab";
import BudgetEntryTab  from "./budget/BudgetEntryTab";
import TrackingTab     from "./budget/TrackingTab";
import CashForecastTab from "./budget/CashForecastTab";
import ScenariosTab    from "./budget/ScenariosTab";
import BusinessPlanTab from "./budget/BusinessPlanTab";
import CompareTab      from "./budget/CompareTab";
import ProjectsTab     from "./budget/ProjectsTab";

const SAAS_MODE = process.env.REACT_APP_SAAS_MODE === 'true';
const PLAN_RANK = { solo: 0, tpe: 1, pme: 2, groupe: 3 };
function userPlan() {
  try { const p = JSON.parse(atob((localStorage.getItem("token") || "..").split(".")[1])); return p.plan || "solo"; }
  catch { return "solo"; }
}

const TABS = [
  { id: "dashboard", label: "Tableau de bord",        icon: "📊", plan: "solo"   },
  { id: "saisie",    label: "Saisie budget",           icon: "✏️",  plan: "solo"   },
  { id: "suivi",     label: "Budget vs Réalisé",       icon: "📋", plan: "solo"   },
  { id: "tresorerie",label: "Prév. trésorerie",        icon: "💧", plan: "tpe"    },
  { id: "scenarios", label: "Scénarios",               icon: "🔀", plan: "pme"    },
  { id: "compare",   label: "Comparaison",             icon: "⚖️",  plan: "pme"    },
  { id: "projets",   label: "Projets",                 icon: "🏗️", plan: "tpe"    },
  { id: "bplan",     label: "Business Plan",           icon: "🚀", plan: "groupe" },
];

export default function Budget({ currentCompanyId }) {
  const [tab, setTab]               = useState("dashboard");
  const [scenarios, setScenarios]   = useState([]);
  const [activeScenario, setActiveScenario] = useState(null);
  const [year, setYear]             = useState(new Date().getFullYear());
  const [loadingScen, setLoadingScen] = useState(false);

  const plan = userPlan();
  const rank = PLAN_RANK[plan] ?? 0;

  const loadScenarios = useCallback(() => {
    if (!currentCompanyId) return;
    setLoadingScen(true);
    api.budget.scenarios(currentCompanyId, year)
      .then(rows => {
        setScenarios(rows);
        // Auto-select first active scenario if none selected
        if (!activeScenario && rows.length > 0) {
          const first = rows.find(s => s.status !== 'archivé') || rows[0];
          setActiveScenario(first);
        }
      })
      .catch(console.error)
      .finally(() => setLoadingScen(false));
  }, [currentCompanyId, year]);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  if (!currentCompanyId) {
    return (
      <div style={{ textAlign: "center", padding: 80, color: "var(--text-muted)" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
        <div style={{ fontSize: 16, fontWeight: 600 }}>Sélectionnez une société pour accéder au Budget</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 0 }}>
        <div style={{ flex: 1 }}>
          <h2 className="page-title" style={{ marginBottom: 2 }}>Budget & Prévisionnel</h2>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Contrôle de gestion · Suivi budget/réalisé · Prévisionnel trésorerie · Business Plan
          </div>
        </div>

        {/* Scenario selector + year */}
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <select
            value={year}
            onChange={e => { setYear(parseInt(e.target.value)); setActiveScenario(null); }}
            style={{ padding: "6px 10px", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 8, fontSize: 12, background: "var(--input-bg,#fff)" }}
          >
            {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          <select
            value={activeScenario?.id || ""}
            onChange={e => {
              const sc = scenarios.find(s => String(s.id) === e.target.value);
              setActiveScenario(sc || null);
            }}
            style={{ padding: "6px 10px", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 8, fontSize: 12, background: "var(--input-bg,#fff)", minWidth: 200 }}
          >
            <option value="">-- Scénario --</option>
            {scenarios.map(s => (
              <option key={s.id} value={s.id}>{s.name} ({s.type}){s.status === 'validé' ? ' ✓' : ''}</option>
            ))}
          </select>

          {activeScenario && (
            <span style={{
              fontSize: 11, padding: "3px 9px", borderRadius: 10, fontWeight: 600,
              background: activeScenario.status === 'validé' ? "#dcfce7" : activeScenario.status === 'archivé' ? "#f3f4f6" : "#fffbeb",
              color: activeScenario.status === 'validé' ? "#16a34a" : activeScenario.status === 'archivé' ? "#6b7280" : "#d97706",
            }}>
              {activeScenario.status}
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 2, borderBottom: "2px solid var(--border-color,#e2e8f0)", marginBottom: 20, marginTop: 16, overflowX: "auto" }}>
        {TABS.map(t => {
          const locked = SAAS_MODE && t.plan && rank < PLAN_RANK[t.plan];
          return (
            <button key={t.id} onClick={() => !locked && setTab(t.id)} style={{
              padding: "9px 16px", fontSize: 13, fontWeight: tab === t.id ? 700 : 500,
              cursor: locked ? "default" : "pointer", border: "none", background: "transparent",
              color: locked ? "var(--text-muted)" : tab === t.id ? "var(--g)" : "var(--text-muted)",
              borderBottom: `2px solid ${tab === t.id ? "var(--g)" : "transparent"}`,
              marginBottom: -2, whiteSpace: "nowrap", transition: "all 0.12s",
              display: "flex", alignItems: "center", gap: 6, opacity: locked ? 0.5 : 1,
            }}>
              <span>{t.icon}</span>
              <span>{t.label}</span>
              {locked && <span style={{ fontSize: 9, background: "#6b7280", color: "#fff", padding: "1px 4px", borderRadius: 5 }}>🔒 {t.plan?.toUpperCase()}</span>}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === "dashboard" && (
        <DashboardTab scenarioId={activeScenario?.id} planRank={rank} />
      )}
      {tab === "saisie" && (
        <BudgetEntryTab
          scenarioId={activeScenario?.id}
          scenario={activeScenario}
          currentCompanyId={currentCompanyId}
          onRefresh={loadScenarios}
        />
      )}
      {tab === "suivi" && (
        <TrackingTab
          scenarioId={activeScenario?.id}
          scenario={activeScenario}
          currentCompanyId={currentCompanyId}
        />
      )}
      {tab === "tresorerie" && (
        <CashForecastTab
          scenarioId={activeScenario?.id}
          currentCompanyId={currentCompanyId}
          planRank={rank}
        />
      )}
      {tab === "scenarios" && (
        rank >= PLAN_RANK["pme"] || !SAAS_MODE
          ? <ScenariosTab
              scenarios={scenarios}
              activeScenarioId={activeScenario?.id}
              onSelect={(sc) => setActiveScenario(sc)}
              onRefresh={loadScenarios}
              currentCompanyId={currentCompanyId}
              planRank={rank}
            />
          : <div style={{ textAlign: "center", padding: 80 }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Multi-scénarios — Plan PME requis</div>
            </div>
      )}
      {tab === "compare" && (
        rank >= PLAN_RANK["pme"] || !SAAS_MODE
          ? <CompareTab scenarios={scenarios} currentCompanyId={currentCompanyId} />
          : <div style={{ textAlign: "center", padding: 80 }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Comparaison multi-scénarios — Plan PME requis</div>
            </div>
      )}
      {tab === "projets" && (
        <ProjectsTab currentCompanyId={currentCompanyId} scenarios={scenarios} />
      )}
      {tab === "bplan" && (
        <BusinessPlanTab scenarioId={activeScenario?.id} planRank={rank} />
      )}
    </div>
  );
}
